import uniqid from 'uniqid';
import omit from 'lodash/omit';
import { setProjectState, getProjectState, viewportActions } from '@starflow/app/store';
import {
  createFlowWithNodes,
  updateUnitResult,
  updateUnitData,
  createUnit,
  findMatchedResultByUrl,
  findUnitRelatedElement,
  findFlowNextNodeId,
  findElementRelatedUnit,
  findFlowRelatedElement,
  checkReachLastNode,
  saveFlowWhenUpdate,
  extendFlow,
  resetFlowAfterNode,
  resolveNodesInputs,
  findPreviousUnits,
  applyOutputToNode,
} from '@starflow/app/helpers/flow';
import { updateElement, insertElement, moveElementToTop } from '@starflow/app/helpers/canvas';
import { getProcessor } from '@starflow/app/helpers/flow';
import { findInsertPosition } from '@starflow/app/helpers/element';
import { tryExecute } from '../../utils/base';
import { notifyWarn } from '@shakkerai/pro-components';
import { eventBus } from '@starflow/app/helpers';
import { createUniqid } from '../../utils';

const locateOptions = { animate: true, fitView: true, paddings: [100, 100, 100, 300] };
const stripUrlToken = (url) => (url ? url.replace(/\?Token=[^&]+/, '') : '');

const isLastNodeInFlow = (lastNodeData, flows) => {
  if (!lastNodeData) return false;

  const flow = flows.find((item) => item.flowId === lastNodeData.flowId);
  console.log(`[DEBUG] flow`, flow);

  return flow?.nodeOrderList?.length && flow.nodeOrderList.at(-1) === lastNodeData.id;
};

export const enterDerivativeFromElement = async (
  type,
  elementId,
  { forceCreate = false, shouldCreateFlow = true } = {},
) => {
  const { elements, flows } = getProjectState();
  const element = elements.find((item) => item.id === elementId);

  if (!element || element.type !== 'image') return;

  const updatedState = { selection: [elementId], editingElementId: element.id, currentElementTool: type };

  // 如果是重新创建，则不找之前的关联节点了，强制新开一个
  const relatedUnit = findElementRelatedUnit(element.id, type);

  let newFlowId = element.flowId;
  let newUnitId = null;

  if (relatedUnit && !forceCreate) {
    // 如果已存在工作单元，则选中工作单元
    updatedState.currentNodeId = relatedUnit.id;
  } else {
    const processor = getProcessor(type);

    if (!processor) return;

    const data = tryExecute(processor.defaultData, { sourceElement: element });
    const tailNode = { id: `node-${createUniqid(type)}`, label: processor.title, type, data, inputs: [], result: {} };

    // 如果不存在工作单元，则创建流或者工作单元
    let nodes = [];

    if (shouldCreateFlow && element.innerTaskId) {
      try {
        nodes = await findPreviousUnits(
          relatedUnit && relatedUnit.data?.result?.innerTaskId ? relatedUnit.data.result : element,
        );
      } catch (error) {
        console.log(`[DEBUG] error`, error);
      }
    }

    /**
     * 没有节点 则创建一个工作单元
     * 只有一个节点 则创建一个流
     * 多个节点 就需要判断是延展流还是创建新流
     * 创建新流需要切断画布元素与之前的联系
     *  */

    if (processor.getInputs) {
      // 调用处理器的getInputs方法来获取输入
      const inputs = processor.getInputs(data);

      // 暂时只处理图片类型的输入
      const imageInputs = inputs.filter((item) => item.value && item.type === 'image');

      for (const item of imageInputs) {
        if (item.value.innerTaskId) {
          tailNode.inputs.push({
            name: item.name,
            type: item.type,
            value: item.value,
            label: item.label,
            __id: item.value.innerTaskId,
          });
        }
      }
    }

    if (nodes.length === 0) {
      newUnitId = createUnit(tailNode, { autoSelect: false });
    } else if (nodes.length === 1) {
      [newFlowId, newUnitId] = createFlowWithNodes(resolveNodesInputs(nodes, tailNode));
    } else {
      // 找到回溯出来的节点里面的最后一个
      // 然后看看这个节点是不是在所属流里面的最后一个节点
      // 如果是，则延展这个流，否则创建新的流

      // const lastNodeData = nodeMap[nodes.at(-1)?.data?.unitId];
      const shouldExtendFlow = isLastNodeInFlow(relatedUnit, flows);

      // 延展现有流
      if (shouldExtendFlow) {
        console.log(`[DEBUG] extend flow with node: `, tailNode);

        [newFlowId, newUnitId] = extendFlow(relatedUnit.flowId, {
          nodes: [tailNode],
          edges: [
            {
              id: uniqid(),
              source: relatedUnit.id,
              target: tailNode.id,
              targetHandle: tailNode.inputs[0]?.name,
              sourceHandle: tailNode.id,
              targetIndex: 0,
            },
          ],
        });

        console.log(`[DEBUG] newFlowId, newUnitId`, { newFlowId, newUnitId });
      } else {
        [newFlowId, newUnitId] = createFlowWithNodes(resolveNodesInputs(nodes, tailNode));
      }
    }

    updatedState.currentNodeId = newUnitId;
  }

  // 进入编辑态
  setProjectState(updatedState);

  setTimeout(() => {
    viewportActions.locateElement(element.id, locateOptions);
    updateElement(
      element.id,
      {
        lastDerivativeUnitId: updatedState.currentNodeId,
        flowId: newFlowId,
      },
      { __skipTracking: true },
    );
  }, 50);
};

export const enterDerivativeFromUnit = async (type, data) => {
  const { id: unitId, flowId } = data;
  const { nodeMap } = getProjectState();
  const unit = nodeMap[unitId];

  if (!unit) return;

  const updatedState = { currentNodeId: unitId, currentElementTool: type };
  const relatedElement = flowId ? findFlowRelatedElement(flowId) : findUnitRelatedElement(unitId);

  console.log(`[DEBUG] relatedElement flowId: ${flowId} unitId: ${unitId}`, relatedElement);

  let relatedElementId = relatedElement?.id;
  const resultUrl = unit?.data?.result?.url;
  const sourceElement = unit?.data?.data?.sourceElement;

  // 节点既没有result也没有sourceElement, 出提示
  if (!resultUrl && !sourceElement) {
    notifyWarn('该节点没有图片, 请先生成图片');
    return;
  }

  const matchedResult = findMatchedResultByUrl(unitId);

  if (!relatedElementId) {
    // 如果不存在画布元素，则需要从单元中获取图片信息并插入画布, 如果有结果图则插入结果图，否则插入原图
    if (resultUrl && matchedResult) {
      const width = matchedResult.width || 512;
      const height = matchedResult.height || 512;
      const [x, y] = findInsertPosition({ width, height });

      relatedElementId = insertElement(
        {
          ...matchedResult,
          id: undefined,
          flowId,
          lastDerivativeUnitId: unitId,
          type: 'image',
          width,
          height,
          x,
          y,
        },
        {
          skipTracking: true,
        },
      );
    } else if (sourceElement) {
      const elementData = Object.assign({}, sourceElement);
      delete elementData?.id;

      const width = elementData?.width || 512;
      const height = elementData?.height || 512;
      const [x, y] = findInsertPosition({ width, height });

      relatedElementId = insertElement(
        {
          ...elementData,
          id: undefined,
          flowId,
          lastDerivativeUnitId: unitId,
          type: 'image',
          width,
          height,
          x,
          y,
        },
        {
          skipTracking: true,
        },
      );
    }
  }

  updatedState.selection = [relatedElementId];
  updatedState.editingElementId = relatedElementId;

  viewportActions.locateElement(relatedElementId, locateOptions);
  updateElement(
    relatedElementId,
    {
      lastDerivativeUnitId: unitId,
      ...(matchedResult
        ? {
            url: matchedResult.url,
            width: matchedResult?.width || 512,
            height: matchedResult?.height || 512,
          }
        : {}),
      // isEmpty: items.length === 0 && !unit.data?.data?.sourceElement, // 节点既没有result也没有sourceElement, 打个空标记
    },
    { __skipTracking: true },
  );

  if (unit.flowId) {
    saveFlowWhenUpdate(unit.flowId);
  }

  // 进入编辑态
  setProjectState(updatedState);
};

export const applyDerivativeResults = async (_type, taskData, results) => {
  let sourceElement = taskData?.params?.sourceElement;

  if (!sourceElement) {
    return null;
  }

  const newResults = results.map((item) => {
    const url = stripUrlToken(item.previewPath);

    return {
      id: item.id,
      innerTaskId: taskData.taskId,
      innerImageId: item.id,
      width: item.width || taskData.width || 1024,
      height: item.height || taskData.height || 1024,
      unitId: taskData.unitId,
      flowId: taskData.flowId,
      url: url,
      type: 'image',
      data: {
        seed: item.imageInfo?.match?.(/Seed: (\d+),/)?.[1],
        illegal: item.illegal,
      },
    };
  });

  const firstResult = newResults[0];

  if (taskData.unitId) {
    updateUnitResult(taskData.unitId, (data) => ({
      ...data,
      url: firstResult.url,
      items: newResults.concat(data?.items || []),
    }));

    eventBus.emit(`task_finish_${taskData.unitId}`, { unitId: taskData.unitId, results: newResults || [] });
  }

  // 如果是自动运行, 最后一个节点将结果发送到画布, 否则更新下一个节点的sourceElement
  if (taskData.isAuto) {
    const isReachLastNode = checkReachLastNode(taskData.flowId);
    if (isReachLastNode) {
      insertElement({
        ...firstResult,
        type: 'image',
        x: taskData.x,
        y: taskData.y,
        flowId: taskData.flowId,
      });
    } else {
      const nextNodeId = findFlowNextNodeId(taskData.flowId, taskData.unitId);
      updateUnitData(nextNodeId, {
        sourceElement: firstResult,
      });
      applyOutputToNode(taskData.flowId, firstResult);
    }
  }

  const sourceElementId = sourceElement.id;
  const editingElementId = getProjectState('editingElementId');
  const elementIds = getProjectState('elements').map((item) => item.id);
  // 如果当前不是在编辑态, 也需要更新下图片状态, 如果在流里, 还需要一些额外的操作
  if (editingElementId !== sourceElementId && elementIds.includes(sourceElementId)) {
    updateElement(sourceElementId, {
      ...omit(firstResult, ['id']),
      topTitle: `${taskData.params.unitLabel} - 100%`,
    });
    // 与DerivativeResults组件的逻辑重复, 看怎么抽出去
    if (taskData.flowId) {
      moveElementToTop(sourceElementId);
      // 清除当前节点之后所有节点的数据
      resetFlowAfterNode(taskData.flowId, taskData.unitId);
      // 更新下一个节点的sourceElement
      const nextNodeId = findFlowNextNodeId(taskData.flowId, taskData.unitId);

      if (nextNodeId) {
        updateUnitData(nextNodeId, {
          sourceElement: { ...firstResult },
        });
      }
    }
  }

  // 如果是流上的操作, 保存流
  if (taskData.flowId) {
    saveFlowWhenUpdate(taskData.flowId);
  }

  return { results: newResults };
};
